import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Checkbox } from 'ui-components/components/checkbox';
import { noop } from 'ui-components/default-props/noop';
import { isNullIndicator, isNotNullIndicator } from '../../../../services/filtering/collection-filter-mapping-service';

import countryNameMessages from '../../../../components/country-names.messages';

import style from '../common.module.less';

const getSelectFilterArgumentText = (filterBy) => (
  <FormattedMessage
    defaultMessage='Select {filterByDisplayName}'
    description='filters.selectFilterArgument'
    id='ihAWxZ'
    values={{ filterByDisplayName: filterBy.displayName }}
  />
);

const getMultipleSelectedText = () => (
  <FormattedMessage defaultMessage='(Multiple)' description='filters.multipleArgumentSelected' id='oItxSY' />
);

const isCountryFilter = (selectedFilterBy) => selectedFilterBy?.id === 'country';

const getCountryLocalizedName = (countryCode) => {
  const countryMessage = countryNameMessages[countryCode];
  if (!countryMessage) {
    return null;
  }
  return <FormattedMessage {...countryMessage} />;
};

const mapExistingFiltersToCriteriaArguments = (existingFilters, criterionArgumentOptions) => {
  const criteriaArguments = existingFilters
    .map((filterObject) => {
      const { criterionArgumentValue } = filterObject;
      return criterionArgumentOptions.find((option) => option.value === criterionArgumentValue);
    })
    .filter((x) => {
      return x !== undefined;
    });

  return criteriaArguments != null && criteriaArguments.length === 0 ? null : criteriaArguments;
};

const isArgumentOptionChecked = (selectedArguments, argumentOption) => {
  const optionAsSelected = selectedArguments.find((criterionArgument) => {
    return criterionArgument.value === argumentOption.value;
  });
  return !!optionAsSelected;
};

const onArgumentOptionToggled = (selectedArguments, toggledArgument, updateCriterionArgument) => {
  const currentSelectedArgumentValues = selectedArguments ? selectedArguments.map((criterionArgument) => criterionArgument.value) : [];
  if (currentSelectedArgumentValues.indexOf(toggledArgument.value) === -1) {
    updateCriterionArgument([...selectedArguments, toggledArgument]);
    return;
  }

  const argumentsWithToggledRemoved = selectedArguments.filter((criterionArgument) => criterionArgument.value !== toggledArgument.value);
  updateCriterionArgument(argumentsWithToggledRemoved);
};

const findArgumentDisplayName = (criterionArgumentOptions, criterionArgumentValue, selectedFilterBy) => {
  const criterionArgument = criterionArgumentOptions.find((argument) => argument.value === criterionArgumentValue);
  if (!criterionArgument) {
    return criterionArgumentValue;
  }
  const displayValue = isCountryFilter(selectedFilterBy) ? getCountryLocalizedName(criterionArgument.value) : criterionArgument.displayName;
  return displayValue || criterionArgumentValue;
};

const mapSelectedArgumentsLengthToDropdownLabel = (selectedArguments, criterionArgumentOptions, selectedFilterBy) => {
  const numberOfSelectedArguments = selectedArguments ? selectedArguments.length : 0;

  switch (numberOfSelectedArguments) {
    case 0:
      return getSelectFilterArgumentText(selectedFilterBy);
    case 1:
      return findArgumentDisplayName(criterionArgumentOptions, selectedArguments[0].value, selectedFilterBy);
    default:
      return getMultipleSelectedText();
  }
};

export const FormIsMultiOption = (criteriaInformations, existingFilters) => {
  const { selectedFilterBy, criterionStateMethods, criterionOperatorOptions, criterionArgumentOptions } = criteriaInformations;
  const { getCriterionArgument, updateCriterionArgument, getCriterionOperator, updateCriterionOperator } = criterionStateMethods;

  const selectedCriterionOperator = getCriterionOperator();
  const selectedCriterionDropdownLabel = selectedCriterionOperator.displayName;

  const existingFilterCriterionArguments = mapExistingFiltersToCriteriaArguments(existingFilters, criterionArgumentOptions);
  const formCheckedArguments = getCriterionArgument();
  const selectedArguments = formCheckedArguments || existingFilterCriterionArguments || [];
  const dropdownLabel = mapSelectedArgumentsLengthToDropdownLabel(selectedArguments, criterionArgumentOptions, selectedFilterBy);
  const shouldShowSupplementalDropdown =
    selectedCriterionOperator.id !== isNullIndicator && selectedCriterionOperator.id !== isNotNullIndicator;

  return (
    <div className={style['form-criteria-section']}>
      <DropdownMenu className={style['filter-dropdown-button']} dropdownMenuShouldHaveWidthOfButton>
        <DropdownMenuToggler>{selectedCriterionDropdownLabel}</DropdownMenuToggler>
        {criterionOperatorOptions.map((option, index) => (
          <DropdownMenuItem key={index} className={style['checklist-row']} onClick={() => updateCriterionOperator(option)}>
            {option.displayName}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
      {shouldShowSupplementalDropdown && (
        <DropdownMenu className={style['filter-dropdown-button']} dropdownMenuShouldHaveWidthOfButton>
          <DropdownMenuToggler>{dropdownLabel}</DropdownMenuToggler>
          {criterionArgumentOptions.map((option, index) => {
            const optionDisplayName = isCountryFilter(selectedFilterBy) ? getCountryLocalizedName(option.value) : option.displayName;
            return (
              <DropdownMenuItem
                key={index}
                className={style['checklist-row']}
                onClick={() => onArgumentOptionToggled(selectedArguments, option, updateCriterionArgument)}
              >
                <Checkbox checked={isArgumentOptionChecked(selectedArguments, option)} label={optionDisplayName} onChange={noop} />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenu>
      )}
    </div>
  );
};
